import { useEffect, useRef, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

type RevealPreset = "default" | "small" | "large" | "card" | "footer";

interface ScrollRevealOptions {
  preset?: RevealPreset;
  /** Stagger delay in ms (applied via CSS custom property) */
  staggerIndex?: number;
  /** Stagger interval in ms (default 90) */
  staggerInterval?: number;
  /** Max items to stagger (rest get base delay 0) */
  staggerMax?: number;
  /** Additional base delay in ms before animation starts */
  delay?: number;
  /** Use eager trigger for elements visible on page load (lower threshold, no negative rootMargin) */
  eager?: boolean;
  /** Keep element fully opaque — only translateY animates. Prevents images from fading in from nothing. */
  keepOpaque?: boolean;
}

const PRESET_CONFIG: Record<RevealPreset, { translateY: number; duration: number }> = {
  default: { translateY: 18, duration: 1000 },
  small: { translateY: 12, duration: 1000 },
  large: { translateY: 22, duration: 1200 },
  card: { translateY: 16, duration: 1000 },
  footer: { translateY: 10, duration: 900 },
};

const EASING = "cubic-bezier(0.16, 1, 0.30, 1)";

/**
 * Returns a ref to attach to the element that should reveal on scroll.
 * Uses IntersectionObserver with threshold 0.2, triggers once.
 * Respects prefers-reduced-motion.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: ScrollRevealOptions = {}
) {
  const ref = useRef<T>(null);
  const { preset = "default", staggerIndex, staggerInterval = 90, staggerMax, delay: baseDelay = 0, eager = false, keepOpaque = false } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const config = PRESET_CONFIG[preset];

    if (prefersReduced) {
      el.style.opacity = "1";
      el.style.transform = "none";
      return;
    }

    // Set initial state — batch with rAF to avoid forced reflow on mount
    requestAnimationFrame(() => {
      if (!keepOpaque) el.style.opacity = "0";
      el.style.transform = `translateY(${config.translateY}px)`;
      el.style.willChange = keepOpaque ? "transform" : "opacity, transform";
    });

    let delay = baseDelay;
    if (staggerIndex !== undefined) {
      const cappedIndex = staggerMax !== undefined ? Math.min(staggerIndex, staggerMax) : staggerIndex;
      delay += cappedIndex * staggerInterval;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            el.style.transition = keepOpaque
              ? `transform ${config.duration}ms ${EASING} ${delay}ms`
              : `opacity ${config.duration}ms ${EASING} ${delay}ms, transform ${config.duration}ms ${EASING} ${delay}ms`;
            if (!keepOpaque) el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            setTimeout(() => {
              el.style.willChange = "auto";
            }, config.duration + delay + 50);
          });
          observer.unobserve(el);
        }
      },
      {
        threshold: eager ? 0.05 : 0.15,
        rootMargin: eager ? "0px" : "0px 0px -18% 0px",
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [preset, staggerIndex, staggerInterval, staggerMax, baseDelay, eager, keepOpaque]);

  return ref;
}

/**
 * Hook for staggered grid children. Returns a function that gives
 * you the ref for each child by index.
 */
export function useStaggerReveal(
  count: number,
  options: { preset?: RevealPreset; interval?: number; keepOpaque?: boolean } = {}
) {
  const isMobile = useIsMobile();
  const maxStagger = isMobile ? 4 : 6;
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const { preset = "card", interval = 90, keepOpaque = false } = options;

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const config = PRESET_CONFIG[preset];

    // Batch initial style writes in rAF to avoid forced reflow during mount
    requestAnimationFrame(() => {
      refs.current.forEach((el) => {
        if (!el) return;
        if (prefersReduced) {
          el.style.opacity = "1";
          el.style.transform = "none";
          return;
        }
        if (!keepOpaque) el.style.opacity = "0";
        el.style.transform = `translateY(${config.translateY}px)`;
        el.style.willChange = keepOpaque ? "transform" : "opacity, transform";
      });
    });

    if (prefersReduced) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const idx = parseInt(el.dataset.staggerIdx || "0", 10);
            const cappedIdx = Math.min(idx, maxStagger);
            const delay = cappedIdx * interval;

            requestAnimationFrame(() => {
              el.style.transition = keepOpaque
                ? `transform ${config.duration}ms ${EASING} ${delay}ms`
                : `opacity ${config.duration}ms ${EASING} ${delay}ms, transform ${config.duration}ms ${EASING} ${delay}ms`;
              if (!keepOpaque) el.style.opacity = "1";
              el.style.transform = "translateY(0)";
              setTimeout(() => {
                el.style.willChange = "auto";
              }, config.duration + delay + 50);
            });

            observerRef.current?.unobserve(el);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -18% 0px",
      }
    );

    refs.current.forEach((el) => {
      if (el) observerRef.current?.observe(el);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [count, preset, interval, maxStagger, keepOpaque]);

  const setRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      refs.current[index] = el;
      if (el) {
        el.dataset.staggerIdx = String(index);
      }
    },
    []
  );

  return { setRef };
}
